import { type FC, useState } from "react";
import { Form } from "react-bootstrap";
import type { AspectRatio } from "shared";
import { useCompany } from "../company/useCompany";
import { Button } from "../components/Button";
import { ContentLoading } from "../components/ContentLoading";
import { useMediaActions } from "../content/useMediaActions";

type Props = {
	postContent: string;
	onImageAttached: (mediaId: number) => void;
};

type ViewState = "form" | "generating" | "preview";

export const PostImageGenerator: FC<Readonly<Props>> = ({
	postContent,
	onImageAttached,
}) => {
	const company = useCompany();
	const { generateFromPost, saveGeneratedV2, invalidateMedia } =
		useMediaActions();

	const [viewState, setViewState] = useState<ViewState>("form");
	const [aspectRatio, setAspectRatio] = useState<AspectRatio>("1:1");
	const [includeLogo, setIncludeLogo] = useState(false);
	const [includeBrandColors, setIncludeBrandColors] = useState(false);
	const [caption, setCaption] = useState("");
	const [generatedImageUrl, setGeneratedImageUrl] = useState("");
	const [generatedPrompt, setGeneratedPrompt] = useState("");

	const hasLogo = !!company.logo_url;
	const hasBrandColors =
		company.brand_colors && company.brand_colors.length > 0;

	const handleGenerate = async () => {
		if (!postContent.trim()) return;
		setViewState("generating");
		try {
			const result = await generateFromPost.mutateAsync({
				postContent,
				aspectRatio,
				includeLogo,
				includeBrandColors,
				caption: caption || undefined,
			});
			setGeneratedImageUrl(result.imageUrl);
			setGeneratedPrompt(result.prompt);
			setViewState("preview");
		} catch {
			setViewState("form");
		}
	};

	const handleRegenerate = () => {
		setViewState("form");
		setGeneratedImageUrl("");
		setGeneratedPrompt("");
	};

	const handleSaveAndAttach = async () => {
		const result = await saveGeneratedV2.mutateAsync(generatedImageUrl);
		await invalidateMedia();
		onImageAttached(result.mediaId);
		setViewState("form");
		setGeneratedImageUrl("");
		setGeneratedPrompt("");
	};

	if (viewState === "generating") {
		return (
			<div className="text-center py-4">
				<ContentLoading />
				<p className="mt-2 mb-0 text-muted">
					Generating image from your post...
				</p>
			</div>
		);
	}

	if (viewState === "preview") {
		return (
			<div>
				<div className="text-center mb-3">
					<img
						src={generatedImageUrl}
						alt="Generated preview"
						style={{
							maxWidth: "100%",
							maxHeight: "300px",
							borderRadius: "8px",
						}}
					/>
				</div>
				{generatedPrompt && (
					<div className="mb-3">
						<small className="text-muted">
							<strong>Generated prompt:</strong> {generatedPrompt}
						</small>
					</div>
				)}
				<div className="d-flex gap-2">
					<Button
						variant="ghost"
						icon="refresh"
						onClick={handleRegenerate}
						disabled={saveGeneratedV2.isPending}
					>
						Regenerate
					</Button>
					<Button
						variant="primary"
						icon="image"
						onClick={handleSaveAndAttach}
						loading={saveGeneratedV2.isPending}
					>
						Attach to Post
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div>
			<div className="mb-3">
				<span className="form-label d-block">Aspect Ratio</span>
				<div className="d-flex gap-2">
					{(
						[
							{ value: "9:16", label: "Story (9:16)" },
							{ value: "4:5", label: "Feed (4:5)" },
							{ value: "1:1", label: "Square (1:1)" },
						] as const
					).map((option) => (
						<Form.Check
							key={option.value}
							type="radio"
							id={`post-img-aspectRatio-${option.value}`}
							name="postImgAspectRatio"
							label={option.label}
							checked={aspectRatio === option.value}
							onChange={() => setAspectRatio(option.value)}
						/>
					))}
				</div>
			</div>

			<div className="mb-2">
				<Form.Check
					type="checkbox"
					id="post-img-includeLogo"
					label="Include company logo"
					checked={includeLogo}
					onChange={(e) => setIncludeLogo(e.target.checked)}
					disabled={!hasLogo}
				/>
				{!hasLogo && (
					<small className="text-muted d-block mt-1">
						No logo uploaded for this company
					</small>
				)}
			</div>

			<div className="mb-3">
				<Form.Check
					type="checkbox"
					id="post-img-includeBrandColors"
					label="Include brand colors"
					checked={includeBrandColors}
					onChange={(e) => setIncludeBrandColors(e.target.checked)}
					disabled={!hasBrandColors}
				/>
				{hasBrandColors && (
					<div className="d-flex gap-1 mt-1">
						{company.brand_colors?.map((color) => (
							<div
								key={color}
								style={{
									width: "20px",
									height: "20px",
									backgroundColor: color,
									borderRadius: "4px",
									border: "1px solid #ccc",
								}}
								title={color}
							/>
						))}
					</div>
				)}
			</div>

			<div className="mb-3">
				<Form.Label>Text Overlay (optional)</Form.Label>
				<Form.Control
					type="text"
					placeholder="Text to display on the image"
					value={caption}
					onChange={(e) => setCaption(e.target.value)}
					maxLength={200}
				/>
				<small className="text-muted">
					This text will be overlaid on the generated image
				</small>
			</div>

			<Button
				icon="bolt"
				variant="primary"
				onClick={handleGenerate}
				loading={generateFromPost.isPending}
				disabled={!postContent.trim()}
			>
				Generate Image
			</Button>
			{!postContent.trim() && (
				<small className="text-muted d-block mt-1">
					Write your post copy first to generate a matching image
				</small>
			)}
		</div>
	);
};
